import type { LevelDefinition } from "@/levels/types";

export interface PaceConfig {
  paceParMs: number;
  pacePointsPerSecond: number;
  maxPaceBonus: number;
}

/** Inert fallback for a level with no `pace` config (only ever a synthetic test level - every shipped level defines one) - always resolves to 0, never guessed at runtime. */
export const INERT_PACE_CONFIG: PaceConfig = { paceParMs: 0, pacePointsPerSecond: 0, maxPaceBonus: 0 };

export function paceConfigForLevel(level: LevelDefinition): PaceConfig {
  return level.pace ?? INERT_PACE_CONFIG;
}

/**
 * `secondsAhead = max(0, floor((paceParMs - activeDurationMs) / 1000))`,
 * `paceBonus = min(maxPaceBonus, secondsAhead * pacePointsPerSecond)`.
 * Callers decide *whether* to call this at all (a failed run, or a
 * `starfruit_gate` completion, both always get exactly 0 - see
 * `LevelGame`/`GameScene`) - this function itself has no notion of
 * completion reason, it only ever computes the formula above.
 */
export function computePaceBonus(config: PaceConfig, activeDurationMs: number): number {
  const secondsAhead = Math.max(0, Math.floor((config.paceParMs - activeDurationMs) / 1000));
  return Math.min(config.maxPaceBonus, secondsAhead * config.pacePointsPerSecond);
}
